import sdk from '@/sdk';
import createFetchOnce from './utils/createFetchOnce';
import {IGrayInfo, IGrayInfoFromServer, IInvokeArgv} from './interface';
import {useEffect, useState} from 'react';
import getIsInGray from './utils/getIsInGray';
import {EVENTS, eventBus} from './utils/emitter';
import {isNull} from 'lodash-es';
import {GRAY_RELEASE_KEYS} from '../const';
import createCacheFetch from './utils/createCacheFetch';
import {IStatus, STATUS} from '@/Constant';

const getGrayReleaseDataCache = createCacheFetch<IGrayInfoFromServer | null>(sdk.as.getGrayReleaseData);

// @ts-ignore
const fetchData = createFetchOnce<IGrayInfoFromServer | null>(getGrayReleaseDataCache);

let grayInfos: IGrayInfoFromServer | null = null;

let status: IStatus = STATUS.loading;

export const getGrayInfos = () => {
    const keys = Object.keys(GRAY_RELEASE_KEYS).map((key) => {
        return GRAY_RELEASE_KEYS[key];
    });
    if (keys.length) {
        fetchData({
            whiteKey: keys,
        }).then((res) => {
            grayInfos = res;
            status = STATUS.success;
            eventBus.emit(EVENTS.fetchSuccess, grayInfos);
        }).catch(() => {
            status = STATUS.error;
            eventBus.emit(EVENTS.fetchError, null);
        });
    }
};

/**
 * 文档组件场景的请求小流量配置数据
 * @param keys 小流量 key 列表
 */
export const getGrayInfosForDocExtension = async (keys: string[]) => {
    if (keys.length) {
        return getGrayReleaseDataCache({
            whiteKey: keys,
        }).then((res) => {
            return res;
        }).catch((err) => {
            console.error(`文档组件请求小流量配置失败: ${err?.message}`);
            return {};
        });
    }
};

const getGrayInfo = (key: string) => {
    // 如果数据没有回来 返回null 代表无数据返回
    if (!grayInfos) {
        return null;
    }
    const data = grayInfos[key];
    if (data) {
        return {
            isUserInGray: data.isOpen,
            whiteRepoList: data.whiteKblist,
            blackRepoList: data.blackKbList,
            whiteStatus: data.whiteStatus,
            blackStatus: data.blackStatus,
        };
    } else {
        // 数据回来了 如果没有对应key 返回undefined
        return undefined;
    }

};

export const initGray = (key: string) => {
    // 接口未返回时 null 接口已返回 但没有对应key的值 为undefined

    if (!key) {
        console.error('[小流量配置] 没有传入小流量key 请检查代码！');
        return {
            useIsInGray: () => false,
            isInGray: () => false,
            isInGrayPromise: () => Promise.resolve(false),
        };
    }

    const useIsInGray = (data?: IInvokeArgv) => {
        const repositoryGuid = data?.repositoryGuid;
        const neverFullVolume = data?.neverFullVolume || false;
        const grayInfo = getGrayInfo(key);
        const [isInGray, setIsInGray] = useState(getIsInGray({
            repositoryGuid,
            grayInfo,
            neverFullVolume,
        }));

        useEffect(() => {
            const handleFetchSuccess = () => {
                const grayInfo = getGrayInfo(key);
                const isInGray = getIsInGray({
                    repositoryGuid,
                    grayInfo,
                    neverFullVolume,
                });
                setIsInGray(isInGray);
            };
            // 如果repositoryGuid 需要重新计算
            handleFetchSuccess();
            eventBus.once(EVENTS.fetchSuccess, handleFetchSuccess);
            return () => {
                eventBus.off(EVENTS.fetchSuccess, handleFetchSuccess);
            };
        }, [repositoryGuid, neverFullVolume]);

        return isInGray;
    };

    const isInGray = (data?: IInvokeArgv) => {
        const repositoryGuid = data?.repositoryGuid;
        const neverFullVolume = data?.neverFullVolume || false;
        const grayInfo = getGrayInfo(key);
        return getIsInGray({
            repositoryGuid,
            grayInfo,
            neverFullVolume,
        });
    };

    const isInGrayPromise = (data?: IInvokeArgv): Promise<boolean | null> => {
        const repositoryGuid = data?.repositoryGuid;
        const neverFullVolume = data?.neverFullVolume || false;
        return new Promise((rs, rj) => {
            const grayInfo = getGrayInfo(key);
            if (status === STATUS.error) {
                rs(null);
            }
            if (isNull(grayInfo)) {
                eventBus.once(EVENTS.fetchSuccess, () => {
                    const grayInfo = getGrayInfo(key);
                    const isInGray = getIsInGray({
                        repositoryGuid,
                        grayInfo,
                        neverFullVolume,
                    });
                    rs(!!isInGray);
                });
                eventBus.once(EVENTS.fetchError, () => {
                    rs(null);
                });
            } else {
                rs(!!getIsInGray({
                    repositoryGuid,
                    grayInfo,
                    neverFullVolume,
                }));
            }

        });
    };

    return {
        useIsInGray,
        isInGray,
        isInGrayPromise,
    };
};

export const initGrayBatch = (keys: string[]) => {
    if (!keys.length) {
        console.error('[小流量配置] 没有传入小流量key 请检查代码！');
        return {
            isInGrayPromise: () => Promise.resolve({}),
        };
    }

    const isInGrayPromise = (data?: IInvokeArgv): Promise<{[key: string]: boolean | undefined | null}> => {
        const repositoryGuid = data?.repositoryGuid;
        const neverFullVolume = data?.neverFullVolume || false;
        return new Promise((rs) => {
            const result: {[key: string]: boolean | undefined | null} = {};
            keys.forEach(key => {
                const grayInfo = getGrayInfo(key);
                if (isNull(grayInfo)) {
                    eventBus.once(EVENTS.fetchSuccess, () => {
                        const grayInfo = getGrayInfo(key);
                        const isInGray = getIsInGray({
                            repositoryGuid,
                            grayInfo,
                            neverFullVolume,
                        });
                        result[key] = isInGray;
                    });
                } else {
                    result[key] = getIsInGray({
                        repositoryGuid,
                        grayInfo,
                        neverFullVolume,
                    });
                }
            });
            rs(result);
        });
    };

    return {
        isInGrayPromise,
    };
};

/**
 * 文档组件场景的小流量配置的格式化函数
 * @param grayInfos 后端接口返回的小流量配置
 * @param key 小流量 key
 */
const getGrayInfoForDocExtension = (grayInfos: IGrayInfoFromServer, key: string) => {
    if (!grayInfos) {
        return;
    }

    const data = grayInfos[key];
    if (data) {
        return {
            isUserInGray: data.isOpen,
            whiteRepoList: data.whiteKblist,
            blackRepoList: data.blackKbList,
            whiteStatus: data.whiteStatus,
            blackStatus: data.blackStatus,
        };
    }
};

/**
 * 文档组件场景的用于判断是否命中小流量的函数
 * @param smallFlowConfig 小流量配置
 * @param key 小流量 key
 * @param repositoryGuid 知识库 id
 */
export const isInGrayForDocExtension = (
    smallFlowConfig: IGrayInfoFromServer,
    key: string,
    repositoryGuid?: string
) => {
    if (!key) {
        console.error('[文档组件小流量配置] 没有传入小流量key 请检查代码！');
    }

    const grayInfo = getGrayInfoForDocExtension(smallFlowConfig, key);

    // 有开启黑名单 或者 开启白名单，代表按知识库维度小流量
    if (grayInfo?.blackStatus || grayInfo?.whiteStatus) {
        return getIsInGray({
            repositoryGuid,
            grayInfo,
        });
    }

    // 按用户小流量
    return getIsInGray({grayInfo});
};
